import loadable from '@loadable/component';
import BpkButton, {
  BUTTON_TYPES,
  SIZE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import {
  BpkBox,
  BpkBreakpoint,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkSnippet from '@skyscanner-internal/backpack-web/bpk-component-snippet';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import CardRail from '../CardRail';
import Icon, { ICON_SIZE } from '../common/Icon';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  ImageSnippetData,
  ImageSnippetListProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ImageSnippetListProps';

import STYLES from './ImageSnippetList.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const ThemeCardStyleSnippet = ({ snippet }: { snippet: ImageSnippetData }) => (
  // BpkCard (not a layout primitive): its Props type is `children, className,
  // href, padded, blank, atomic` only — no width/height/margin/display/flex
  // props at all. The 0.75rem card-margin (no matching BpkSpacing token) is
  // one of several properties with nowhere to go; even a token-matched value
  // would still have no prop to land on. Same root cause as ThemeCard's own
  // `.ThemeCard` class in ThemeCard.module.scss. See
  // backpack-api-gaps/WOM-6778-image-imagesnippet-gaps.md.
  <BpkCard
    padded={false}
    href={snippet.redirectUrl}
    rel="nofollow"
    className={STYLES.ImageSnippetList__themeCard}
  >
    {/* BpkVessel (not BpkBox/BpkFlex): needs `aspect-ratio` (no prop exists)
        and `background-color: tokens.$bpk-color-white` — a literal white,
        not one of BpkLayoutBackgroundColor's semantic surface/status tokens,
        none of which guarantee literal white in every theme — plus
        `border-radius` (top corners only, typed `never`) and a descendant
        `img` selector sizing the raw <img> child. */}
    <BpkVessel
      as="div"
      className={STYLES.ImageSnippetList__themeCardImageWrapper}
    >
      <img
        src={snippet.imageSrc}
        alt={snippet.altText}
        loading="lazy"
        aria-hidden
      />
    </BpkVessel>
    {/* BpkVessel (not BpkBox/BpkFlex): needs `border-top` (typed `never`)
        and a negative `margin-top` (not a valid BpkMarginValue — only
        tokens/percentages/auto). */}
    <BpkVessel as="div" className={STYLES.ImageSnippetList__themeCardContent}>
      {snippet.headline && (
        // BpkBox wrapper carries the margin BpkText itself can't accept (no
        // margin/padding props exist on BpkText — verified against its Props
        // type). marginTop reset to None since BpkText's tagName="h3" is a
        // native heading with its own UA-stylesheet top margin.
        <BpkBox marginTop={BpkSpacing.None} marginBottom={BpkSpacing.MD}>
          <BpkText tagName="h3" textStyle={TEXT_STYLES.heading4}>
            {snippet.headline}
          </BpkText>
        </BpkBox>
      )}
      {snippet.body && (
        <BpkBox margin={BpkSpacing.None}>
          <BpkText
            tagName="p"
            textStyle={TEXT_STYLES.bodyDefault}
            dangerouslySetInnerHTML={{ __html: snippet.body }}
          />
        </BpkBox>
      )}
      {snippet.footnote && (
        // marginTop="auto" (a valid BpkMarginValue) still pins this to the
        // bottom of `__themeCardContent`'s flex column: the BpkBox wrapper
        // is the actual flex child here, not the BpkText inside it.
        <BpkBox
          marginTop="auto"
          marginBottom={BpkSpacing.None}
          paddingTop={BpkSpacing.LG}
        >
          <BpkText tagName="p" textStyle={TEXT_STYLES.footnote}>
            {snippet.footnote}
          </BpkText>
        </BpkBox>
      )}
    </BpkVessel>
  </BpkCard>
);

const ImageSnippetList = (props: ImageSnippetListProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const { cardStyle, cta, desktopLayout, imageSnippetData, subtitle, title } =
    props;
  const IconComponent = Icon('chevron-right', ICON_SIZE.SMALL);
  const buttonMbdComponentName = `${rawComponentName}_cta`;

  if (!title || imageSnippetData.length < 1) {
    return null;
  }

  return (
    // Outer div previously used className="ImageSnippetList" (plain string — CSS module
    // styles were dead code). Replaced with BpkBox; dead SCSS block removed.
    <BpkBox
      width="100%"
      height="100%"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <BpkSectionHeader title={title} description={subtitle} />
      <CardRail>
        {imageSnippetData.map((snippet: ImageSnippetData) =>
          cardStyle === 'themeCard' ? (
            <ThemeCardStyleSnippet
              key={`image_snippet_${snippet.altText}`}
              snippet={snippet}
            />
          ) : (
            // Original margin was 0.75rem (12px). Nearest token: BpkSpacing.MD (8px, Δ4px)
            // — within the ≤5px near-match threshold. Responsive: margin=0 at mobile
            // (≤512px) via BpkBreakpoint.SmallTablet (min-width 513px).
            <BpkBox
              key={`image_snippet_${snippet.altText}`}
              minWidth="19.71875rem"
              margin={{
                base: BpkSpacing.None,
                [BpkBreakpoint.SmallTablet]: BpkSpacing.MD,
              }}
            >
              <BpkSnippet
                src={snippet.imageSrc}
                altText={snippet.altText}
                headline={snippet.headline}
                headlineStyle="heading-4"
                desktopLayout={desktopLayout}
              />
              {snippet.body && (
                <BpkText
                  textStyle={TEXT_STYLES.bodyDefault}
                  dangerouslySetInnerHTML={{ __html: snippet.body }}
                />
              )}
            </BpkBox>
          ),
        )}
      </CardRail>
      {cta && (
        // BpkButton className kept: button layout (width, height, margin, align-self,
        // border-radius, responsive overrides) cannot be expressed via BpkButton props.
        <BpkButton
          className={STYLES.ctaButton}
          href={cta.url}
          type={BUTTON_TYPES.primary}
          size={SIZE_TYPES.small}
          data-tracking-element-id={buttonMbdComponentName}
          data-tracking-common-params={buildMBDParam(buttonMbdComponentName)}
        >
          {cta.text}
          <IconComponent className={STYLES.icon} />
        </BpkButton>
      )}
    </BpkBox>
  );
};

export default (props: ImageSnippetListProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.IMAGE_SNIPPET_LIST,
      })
    }
  >
    <ImageSnippetList {...props} />
  </IntersectionObserverWrapper>
);
